import React, {useEffect, useState} from "react";
import {AutoComplete, Button, Empty, Modal, notification, Select, Spin} from "antd";
import {useAuth} from "oidc-react";
import {useNavigate} from "react-router-dom";
import {useJwt} from "react-jwt";
import {convertFileToBase64, getFileExtension} from "../../../utils/file.utils";
import SingleFileUploader from "../../Uploader/SingleFileUploader";
import SupplierOptionCard from "../../Options/Supplier";
import SharedService from "../../../services/shared/service";

const {Option} = Select;

const Label = ({children, required}) => (
    <div className="text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide mb-1.5">
        {children}
        {required && <span style={{color: "#A4231D"}}> *</span>}
    </div>
);

const InvoicePopup = ({visible, onClose, fetchInvoiceSearchData}) => {
    const [fileList, setFileList] = useState([]);
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [companySearch, setCompanySearch] = useState("");
    const [supplierSearchText, setSupplierSearchText] = useState("");
    const [supplierSelectedValue, setSupplierSelectedValue] = useState(undefined);
    const [selectedVesselId, setSelectedVesselId] = useState(null);
    const [customerType, setCustomerType] = useState(null);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [selectedSupplierId, setSelectedSupplierId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [supplierLoading, setSupplierLoading] = useState(false);

    const {userData, userManager} = useAuth();
    const token = userData?.access_token;
    const {decodedToken} = useJwt(token);
    const userEmail = decodedToken?.email;
    const navigate = useNavigate();

    useEffect(() => {
        const interval = setInterval(() => {
            userManager?.signinSilent().catch(() => {});
        }, 600000);
        return () => clearInterval(interval);
    }, [userManager]);

    const handleCompanySearch = async (value) => {
        setCompanySearch(value);
        setCompanyOptions([]);

        if (!value || value.length < 2) return;
        setCustomerLoading(true);
        try {
            const response = await SharedService.post({
                token,
                url: "customer/search/autocomplete",
                data: {searchTerm: value},
                params: {select: "id,displayName,name,accountingType,vesselId,vesselName"},
            });
            const options = (response ?? []).map((item) => ({
                label: `${item.displayName}${item?.vesselName ? ` - (${item.vesselName})` : ""}`,
                value: `${item.displayName}${item?.vesselName ? ` - (${item.vesselName})` : ""}`,
                VesselId: item.vesselId,
                id: item.id,
                VesselName: item?.vesselName,
                customerName: item.name,
                CoyId: item.id,
                AccountingType: item.accountingType,
                consolidateDetailLines: item.consolidateDetailLines,
            }));
            setCompanyOptions(options);
        } catch (error) {
            if (error?.response?.status === 500) {
                notification.error({message: "Error", description: error.response.statusText});
            }
        } finally {
            setCustomerLoading(false);
        }
    };

    const handleCompanySelect = (_value, option) => {
        setSelectedCustomerId(option?.id);
        setSelectedVesselId(option?.VesselId);
        setCustomerType(option?.AccountingType);
    };

    useEffect(() => {
        if (!supplierSearchText || supplierSearchText.length <= 2) {
            setSupplierOptions([]);
            return;
        }
        let cancelled = false;
        setSupplierLoading(true);

        SharedService.post({
            token,
            url: "supplier/search/autocomplete",
            data: {searchTerm: supplierSearchText},
            params: {select: "id,name,address,bankDetails,type,isRecoverySupplier,recoveryServiceTypes,purchasesCount"},
        })
            .then((response) => {
                if (cancelled) return;
                setSupplierOptions(response ?? []);
            })
            .catch((error) => {
                if (error?.response?.status === 500) {
                    notification.error({message: "Error", description: error.response.statusText});
                }
            })
            .finally(() => {
                if (!cancelled) setSupplierLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [supplierSearchText, token]);

    const resetForm = () => {
        setCompanySearch("");
        setSupplierSearchText("");
        setSupplierSelectedValue(undefined);
        setSelectedCustomerId(null);
        setSelectedSupplierId(null);
        setSelectedVesselId(null);
        setCustomerType(null);
        setFileList([]);
        setCompanyOptions([]);
        setSupplierOptions([]);
    };

    const handleClose = () => {
        resetForm();
        onClose?.();
    };

    const fetchDocumentById = async (documentId, duplicateMessage) => {
        try {
            const response = await SharedService.get({
                token,
                url: `document/${documentId}`,
            });
            navigate(`/details/${documentId}`);

            const parts = (duplicateMessage ?? "").split(".");
            const head = parts[0] ? `${parts[0]}.` : "";
            const tail = parts[1] ? parts[1].split(/(\d+)/) : [];

            notification.warning({
                message: "Warning",
                description: (
                    <ul style={{paddingLeft: 16, margin: 0}}>
                        {head && <li>{head}</li>}
                        {tail[0] && <li>{(tail[0] ?? "") + (tail[1] ?? "")}</li>}
                        <li>Uploaded On: {response?.createdOn}</li>
                        <li>Source: {response?.sourceType?.source}</li>
                    </ul>
                ),
            });
        } catch (error) {
            console.error("AddInvoicePopup Error:", error);
        }
    };

    const handleSubmit = async () => {
        if (fileList.length === 0) {
            notification.warning({
                message: "Attach an invoice",
                description: "Please attach an invoice before submitting.",
            });
            return;
        }

        setLoading(true);
        try {
            const base64File = await convertFileToBase64(fileList[0].originFileObj);
            const fileNames = fileList.map((f) => f.name);

            const searchData = {
                base64File,
                CustomerType: customerType,
                CustomerId: selectedCustomerId,
                SupplierId: selectedSupplierId,
                emailAddress: "",
                fileName: fileNames[0].replace(/\.[^/.]+$/, ""),
                fileType: getFileExtension(fileList[0]),
                sourceId: "3",
                vesselId: selectedVesselId,
                messageId: "",
                uploadedBy: userEmail,
            };

            const response = await SharedService.post({
                token,
                url: "invoice/manual",
                data: searchData,
            });

            if (response) fetchInvoiceSearchData?.();

            if (response?.status === "duplicate") {
                await fetchDocumentById(response.documentId, response.message);
            } else {
                notification.success({message: "Success", description: response?.message});
                navigate(`/details/${response.documentId}`);
            }
            resetForm();
            onClose?.();
        } catch (error) {
            notification.error({
                message: "Error",
                description:
                    error?.response?.data?.message ??
                    error?.response?.data?.errors?.[0]?.errorMessage ??
                    error?.response?.statusText ??
                    "Failed to create invoice",
            });
            resetForm();
            onClose?.();
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="New Invoice"
            open={visible}
            onCancel={handleClose}
            width={680}
            closable={true}
            footer={[
                <Button key="cancel" onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                >
                    Create Invoice
                </Button>,
            ]}
        >
            <div className="flex flex-col gap-4">
                <div>
                    <Label>Customer</Label>
                    <AutoComplete
                        allowClear
                        value={companySearch}
                        onChange={handleCompanySearch}
                        onSelect={handleCompanySelect}
                        className="w-full"
                        placeholder="Search customer"
                        options={companyOptions.map((c) => ({
                            value: c.value,
                            id: c.id,
                            VesselId: c.VesselId,
                            AccountingType: c.AccountingType,
                        }))}
                        notFoundContent={
                            customerLoading ? (
                                <Spin className="custom-spin flex justify-center"/>
                            ) : companyOptions.length === 0 ? (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}/>
                            ) : null
                        }
                    />
                </div>

                <div>
                    <Label>Supplier</Label>
                    <Select
                        showSearch
                        allowClear
                        suffixIcon={null}
                        placeholder="Search supplier"
                        className="w-full"
                        popupMatchSelectWidth={550}
                        value={supplierSelectedValue}
                        notFoundContent={
                            supplierLoading ? (
                                <Spin className="custom-spin flex justify-center"/>
                            ) : supplierOptions.length === 0 ? (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}/>
                            ) : null
                        }
                        optionLabelProp="label"
                        defaultActiveFirstOption
                        dropdownStyle={{maxHeight: 600, overflow: "auto"}}
                        onSearch={(value) => setSupplierSearchText(value)}
                        onChange={(value) => setSupplierSelectedValue(value)}
                        onSelect={(_value, option) => {
                            setSelectedSupplierId(option?.id);
                            setSupplierSelectedValue(option?.label);
                        }}
                    >
                        {supplierOptions.map((item) => (
                            <Option
                                key={item.id}
                                value={`${item.name}|${item.id}`}
                                label={item.name}
                                id={item.id}
                            >
                                <SupplierOptionCard supplier={item} isSelected={false}/>
                            </Option>
                        ))}
                    </Select>
                </div>

                <div>
                    <Label required>Invoice File</Label>
                    <SingleFileUploader
                        fileList={fileList}
                        onUpload={({onSuccess}) => onSuccess?.("success")}
                        onChange={(file) => setFileList(file ? [file] : [])}
                        onRemove={() => setFileList([])}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default InvoicePopup;
